import { UserInfoInterface } from 'types/user';

// Decide se o Catálogo de Experiências aparece para o player.
//
// `piloto_catalogo` nasceu como a flag do piloto do 1S2025 e virou, na prática, a resposta
// para "quem pode ver o catálogo". Com a entrada dos acordos PREDEFINIDO ela deixa de ser
// suficiente sozinha — mas continua valendo, para não tirar acesso de quem já tem.
//
// O `valor_diario_pontos` entra aqui porque o trigger `E1djhtF` aborta a geração do catálogo
// quando ele é zero. Sem esse termo, ~13,3 mil predefinidos veriam o item de menu e o botão
// do dashboard só para bater numa página que nunca teria itens. É um gate de UI espelhando
// uma porta que já existe no backend, não uma regra nova.
//
// O piso de pontos (FAIXA I, minPoints 9999) NÃO é replicado aqui: o front não conhece a
// pontuação do ciclo, e essa porta continua sendo do backend.
export function canAccessExperienceCatalog(userInfo?: UserInfoInterface): boolean {
  if (!userInfo) return false;

  const isEligibleProfile = !!userInfo.piloto_catalogo || userInfo.tipo_de_acordo === 'PREDEFINIDO';
  const hasDailyPointValue = typeof userInfo.valor_diario_pontos === 'number' && userInfo.valor_diario_pontos > 0;

  return isEligibleProfile && hasDailyPointValue;
}

// Decide se a ação de desfazer o resgate aparece na página de feedback.
//
// São duas condições, e as duas vêm do backend — esta função só as junta:
//   1. o jogador tem resgate líquido ativo no ciclo (`loadExperiencePurchase` devolveu algo);
//   2. a janela de troca do ciclo ainda não fechou (`periodo_catalogo__c.endDate`).
//
// O "agora" é o relógio do SERVIDOR (`getServerTime()`), nunca o do navegador. Quem recusa
// de verdade é a trigger `before_create` de `desfazer_resgate__c`, que compara com o próprio
// relógio dela; se o front usasse `Date.now()`, um navegador com a hora atrasada ofereceria
// um botão que o backend recusa, e um adiantado esconderia um botão que ainda vale.
//
// A borda é inclusiva: no instante exato do `endDate` ainda dá para desfazer, porque a
// trigger só recusa com `agora > endDate`. Sem prazo conhecido não se oferece a ação — o
// front não tem como saber se a janela está aberta, e prometer menos é mais seguro do que
// prometer o que o backend vai negar.
export function canUndoPurchase(hasActivePurchase: boolean, serverNow: number, deadline?: Date): boolean {
  if (!hasActivePurchase) return false;
  if (!deadline) return false;

  return serverNow <= deadline.getTime();
}

// Tira o prefixo de catalogação do nome da experiência.
//
// Os itens de combo são cadastrados como "(Combo 28) - Turnê Dominguinho Rio de Janeiro +
// Pontos Livelo". O "(Combo 28)" serve à DIPES para casar item com planilha; para quem
// escolhe a experiência é ruído, e ruído caro: o título ocupa 5 linhas no card e empurra o
// resto para fora.
//
// O separador tolera espaçamento variável em vez de casar `") - "` literal, como faz a
// versão AngularJS do relatório. O motivo é medido, não teórico: o Combo 4 aparece como
// "(Combo 4)  - 1 dia livre + Cartão premiação", com DOIS espaços, em 4 documentos de
// `catalogo_experiencias__c`. Com a comparação literal, ele seria o único card a manter o
// prefixo — e uma inconsistência dessas parece bug de conteúdo, não de código.
//
// Nome sem o separador volta intacto: é o caso das três premiações isoladas ("Pontos
// Livelo", "Cartão premiação", "Dias livres"), que não têm prefixo nenhum.
export function stripCatalogPrefix(title?: string): string {
  if (!title) return '';

  const separador = /\)\s*-\s*/;
  const encontrado = title.match(separador);

  if (!encontrado || encontrado.index === undefined) return title;

  return title.slice(encontrado.index + encontrado[0].length);
}

/*
 * A frase do complemento ("1 dia livre + Cartão premiação no valor de R$ 13.776,91") era
 * montada aqui, por `getComboComplementText` e `getComboConfirmationText`. As duas saíram em
 * 2026-08-25: o motor voltou a gravá-la em `extra.description`, como fazia no 2S2025.
 *
 * O que decidiu não foi gosto — foi o payload do resgate. `description` é lido por quem
 * consome o `achievement` fora do front (relatório, exportação, consulta direta), e montar a
 * frase só na tela deixava esses consumidores com o texto estático do item. Com a frase no
 * dado, as três telas apenas exibem `description` e ninguém precisa remontá-la.
 */

/**
 * O título do card de uma premiação isolada.
 *
 * `valor` chega FORMATADO do motor, não como número: o `E1djhtF` emite
 * `formatCurrencyDown` no cartão ("4.792,50") e `String.format("%,.0f")` nos pontos
 * ("177.500"). O tipo do parâmetro diz `number | string` porque é isso que trafega — a
 * interpolação do cartão já dependia disso antes desta assinatura existir.
 *
 * `pontos_Livelo` entrou no mapa em 2026-09-03: o card mostrava só "Pontos Livelo", sem o
 * total, enquanto o cartão ao lado anunciava o valor no próprio título. Os dois são a mesma
 * escolha — quanto o jogador leva —, e só um dizia quanto.
 *
 * Sem `valor` o título recua para o nome do tipo: resgate de ciclo anterior chega sem o
 * campo, e "undefined Pontos Livelo" seria pior que a ausência do número.
 */
export function getExperienceTypeTitle(type: string, valor?: number | string): string {
  const typeMap: Record<string, string> = {
    dias_livres: 'Dias Livres',
    cartao: `Cartão Premiação no valor de R$${valor}`,
    pontos_Livelo: valor ? `${valor} Pontos Livelo` : 'Pontos Livelo',
  };

  // Se a última palavra for 'cd', coloca em uppercase
  const result = typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
  return result.replace(/\bcd\b$/i, 'CD');
}

export function getExperienceTypeDescription(type: string, validity?: string): string {
  const typeMap: Record<string, string> = {
    dias_livres: `Os dias livres devem ser usufruídos até ${validity}`,
    cartao: `Cartão exclusivo para aquisição de esperiências (sem função de saque ou pagamento de contas).`,
  };

  return typeMap[type] || type.charAt(0) + type.slice(1).replace(/_/g, ' ');
}

export function getExperienceTypeIcon(type: string): string {
  const iconMap: Record<string, string> = {
    dias_livres: '🏖️',
    cartao: '💳',
  };

  return iconMap[type] || '🎯';
}
